import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose
} from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UpdateRegisteredUserRequest } from '../../../dto/update-registered-user-request';
import { UserStatus } from '../../../dto/user-status.enum';
import { ValidateInvitations } from '../../../dto/validate-invitations';
import { MfaService } from '../../../services/mfa.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { FlexModule } from '@angular/flex-layout/flex';
import {
  MatFormField,
  MatSuffix,
  MatLabel
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { NgIf } from '@angular/common';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'mfa-update-user',
  templateUrl: './update-user.component.html',
  styleUrls: ['./update-user.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FlexModule,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatTooltip,
    MatIcon,
    MatSuffix,
    MatCheckbox,
    NgIf,
    MatDialogActions,
    MatButton,
    MatDialogClose
  ]
})
export class UpdateUserComponent implements OnInit, OnDestroy {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UpdateRegisteredUserRequest,
    private dialogRef: MatDialogRef<UpdateUserComponent>,
    private mfa: MfaService
  ) {
    if (data.additionalUsernames) {
      this.originalUsernames = [data.username, ...data.additionalUsernames];
    } else {
      this.originalUsernames = [data.username];
    }
    this.originalEmail = data.email;
  }

  ngOnInit(): void {}
  onDestroy$ = new Subject<void>();
  takenUsernames: string[] | null = null;
  emailExists = false;
  validating = false;
  originalUsernames: string[];
  originalEmail: string;
  userStatus = UserStatus;
  isAddClicked = false;
  hasError = false;

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  getAdditionalUserNames() {
    return this.data.additionalUsernames
      ? this.data.additionalUsernames.join(',')
      : '';
  }

  isUserNameExists(): boolean {
    if (this.takenUsernames && this.takenUsernames.length > 0) {
      const userName = this.data.username.includes('\\')
        ? this.data.username.toLowerCase()
        : '.\\' + this.data.username.toLowerCase();

      if (this.takenUsernames.includes(userName)) {
        return true;
      }
    }
    return false;
  }

  aditionalUsersExists(): string {
    const users = (
      this.data.additionalUsernames
        ? [this.data.username, ...this.data.additionalUsernames]
        : [this.data.username]
    ).map((username) =>
      username.includes('\\')
        ? username.toLowerCase()
        : '.\\' + username.toLowerCase()
    );
    for (const user of users) {
      if (users.filter((u) => u === user && user !== '.\\').length > 1) {
        return user;
      }
    }

    if (this.takenUsernames && this.takenUsernames.length > 0) {
      const additionalUsers = this.data.additionalUsernames.map((username) =>
        username.includes('\\')
          ? username.toLowerCase()
          : '.\\' + username.toLowerCase()
      );
      for (const user of additionalUsers) {
        if (this.takenUsernames.includes(user)) {
          return user;
        }
      }
    }
    return '';
  }

  update() {
    if (!this.data.customerId || !this.data.resourceId || !this.data.userId) {
      return;
    }

    this.isAddClicked = true;
    this.validating = true;
    this.takenUsernames = null;
    this.emailExists = false;
    this.normalizeData();
    this.checkData();

    const usernames = (
      this.data.additionalUsernames
        ? [this.data.username, ...this.data.additionalUsernames]
        : [this.data.username]
    )
      .map((username) =>
        username.includes('\\')
          ? username.toLowerCase()
          : '.\\' + username.toLowerCase()
      )
      .filter(
        (u) =>
          !this.originalUsernames
            .map((username) => username.toLowerCase())
            .includes(u)
      );
    if (usernames) {
      this.mfa
        .validateInvitations(
          this.data.customerId,
          this.data.resourceId,
          new ValidateInvitations(
            usernames,
            this.originalEmail === this.data.email ? [] : [this.data.email]
          )
        )
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((taken) => {
          this.validating = false;
          if (taken && taken.usernames.length > 0) {
            this.takenUsernames = taken.usernames.map((username) =>
              username.toLowerCase()
            );
            this.hasError = true;
          }

          if (taken && taken.emails.length > 0) {
            this.emailExists = true;
            this.hasError = true;
          }

          if (!this.hasError) {
            this.isAddClicked = false;
            this.dialogRef.close(this.data);
          }
        });
    } else if (!this.hasError) {
      this.isAddClicked = false;
      this.dialogRef.close(this.data);
    }
  }

  checkData() {
    this.validating = false;
    if (
      !this.data.username ||
      this.data.username.trim() === '' ||
      !this.data.email ||
      this.data.email.trim() === '' ||
      !this.isValidEmail(this.data.email)
    ) {
      this.hasError = true;
    }
    if (
      this.isUserNameExists() ||
      this.emailExists ||
      this.aditionalUsersExists() !== ''
    ) {
      this.hasError = true;
    }
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  normalizeData() {
    this.data.username = this.data.username.trim();
    this.data.email = this.data.email.trim();
    if (Array.isArray(this.data.additionalUsernames)) {
      for (const [index, user] of this.data.additionalUsernames.entries()) {
        this.data.additionalUsernames[index] = user.trim();
      }
    }
  }
}
